import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ECampaignCreateState,
  ECampaignStateStatus,
  ECampaignType,
  ICampaign,
  ICampaignState,
} from 'app/store/models/campaigns.model';
import { IContent } from 'app/store/models/content.model';
import { Store } from '@ngrx/store';
import { EReducers, IReducers } from 'app/store/reducers';
import { createCampaign, getCampaignInfo, updateCampaign } from 'app/store/actions/campaigns.action';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';
import { InfoTipsComponent } from 'app/dialogs/info-tips/info-tips.component';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { ChooseFrequencyComponent } from 'app/dialogs/choose-frequency/choose-frequency.component';
import { HttpErrorResponse } from '@angular/common/http';
import { SessionService } from 'app/store/services/session.service';
import { CampaignService } from 'app/store/services/campaign.service';

@Component({
  selector: 'app-edit-campaign',
  templateUrl: './edit-campaign.component.html',
  styleUrls: ['./edit-campaign.component.scss'],
})
export class EditCampaignComponent implements OnInit, OnDestroy {
  state: ECampaignCreateState;
  campaignId: number;
  campaignInfo: ICampaign;
  componentReady = false;

  campaignNameMissingError: boolean;
  campaignNameTaken: boolean;
  missingFrequency: boolean;

  formGroup: FormGroup;
  sessionData: any;

  createButton: Button = {
    color: ButtonColor.ORANGE,
    text: 'Create Campaign',
    size: ButtonSize.SMALL,
    onClick: () => this.submitForm(),
  };

  updateFreq: Button = {
    color: ButtonColor.BLUE,
    text: 'Change Frequency',
    size: ButtonSize.SMALL,
    style: 'outline',
    onClick: () => this.store.dispatch(
      openDialog({
        dialogData: {
          content: ChooseFrequencyComponent,
          title: 'Choose Frequency',
          canClose: true,
          dialogMinWidth: '400px',
          componentData: {
            initial: this.formGroup.get('campaign_frequency').value?.toString(),
          },
          buttons: [
            {
              color: ButtonColor.BLUE,
              size: ButtonSize.SMALL,
              actAsClose: true,
              text: 'Choose',
              onClick: dialog =>
                this.formGroup
                  .get('campaign_frequency')
                  .setValue(dialog.dialogForm.get('frequency').value),
            },
          ],
        },
      }),
    ),
  };


  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private store: Store<IReducers>,
    private router: Router,
    private sessionService: SessionService,
    private campaignService: CampaignService,
  ) {
  }

  ngOnInit() {
    this.state = this.activatedRoute.snapshot.data.state;
    this.campaignId = this.activatedRoute.snapshot.params.id;
    if (!this.sessionData && this.sessionService.ready?.value) {
      this.sessionData = this.sessionService.ready.value;
    }
    this.sessionService.onReady().pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.sessionData = data;
      if (this.state === ECampaignCreateState.CREATE && data?.c2recruit) {
        this.formGroup?.get('recruit')?.setValue(true);
      }
    });
    if (this.state === ECampaignCreateState.EDIT) {
      if (this.campaignId) {
        this.store.dispatch(
          getCampaignInfo({
            campaign_id: this.campaignId,
          }),
        );
      }
    } else {
      this.initForm();
      this.componentReady = true;
    }
    this.store
      .select(EReducers.CAMPAIGNS)
      .pipe(takeUntil(this.destroy$))
      .subscribe((state: ICampaignState) => {
        if (
          state.status === ECampaignStateStatus.READY &&
          state.currentCampaign
        ) {
          this.campaignInfo = state.currentCampaign;
          this.initForm();
          this.componentReady = true;
        }

        if (state.status === ECampaignStateStatus.UPDATED) {
          this.store.dispatch(openSnackbar({
            snackbarData: {
              data: {
                message: `The campaign was successfully ${this.state === ECampaignCreateState.EDIT ? 'updated' : 'created'}!`,
                type: 'success',
              },
            },
          }));
          this.router.navigate(['/campaigns']);
        }

        if (
          state.status === ECampaignStateStatus.CRATED &&
          state.new_campaign_id
        ) {
          this.store.dispatch(openSnackbar({
            snackbarData: {
              data: {
                message: `The campaign was successfully ${this.state === ECampaignCreateState.EDIT ? 'updated' : 'created'}!`,
                type: 'success',
              },
            },
          }));

          this.router.navigate([
            '/campaigns',
            state.new_campaign_id,
            'communication-content',
          ]);
        }

        if (state.status === ECampaignStateStatus.ERROR) {
          const error: HttpErrorResponse = state.error;

          if (error?.error?.code === 1004) {
            const msg = Array.isArray(error.error.message) ? error.error.message[0] : error.error.message;
            this.store.dispatch(openSnackbar({
              snackbarData: {
                data: {
                  message: msg || 'At least one content should contain Recruit link',
                  type: 'error',
                },
              },
            }));
            return;
          }

          if (error.error.code === 1001) {
            this.campaignNameMissingError = true;
          }

          if (error.error.code === 1002) {
            const errorMessages = error.error.message;
            if (Array.isArray(errorMessages)) {
              errorMessages.forEach(message => {
                try {
                  const messageParsed = JSON.parse(message);

                  if (
                    messageParsed &&
                    typeof messageParsed === 'object' &&
                    messageParsed.frequency
                  ) {
                    this.missingFrequency = true;
                  }
                } catch (e) {
                  console.warn('Could not parse JSON', message);
                }
              });
            }
            if (!this.missingFrequency) {
              this.campaignNameTaken = true;
            }
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next(false);
  }

  initForm() {
    this.formGroup = new FormGroup({
      campaign_type: new FormControl(this.campaignInfo?.campaign_type || ECampaignType.STANDARD),
      campaign_name: new FormControl(
        this.campaignInfo ? this.campaignInfo.campaign_name : '',
        [Validators.required],
      ),
      campaign_frequency: new FormControl(this.campaignInfo?.campaign_frequency || '1'),
      recruit: new FormControl(this.resolveRecruitFlag()),
    });
  }

  onCampaignTypeSelection(event: MatRadioChange) {
    if (event.value === ECampaignType.FREQUENCY && this.state !== ECampaignCreateState.EDIT) {
      this.store.dispatch(
        openDialog({
          dialogData: {
            content: ChooseFrequencyComponent,
            title: 'Choose Frequency',
            canClose: true,
            dialogMinWidth: '400px',
            buttons: [
              {
                color: ButtonColor.BLUE,
                size: ButtonSize.SMALL,
                actAsClose: true,
                text: 'Choose',
                onClick: dialog =>
                  this.formGroup
                    .get('campaign_frequency')
                    .setValue(dialog.dialogForm.get('frequency').value),
              },
            ],
          },
        }),
      );
    }
  }

  onInfoImageClick() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: InfoTipsComponent,
          title: 'Tips',
          canClose: true,
          dialogMinWidth: '700px',
          componentData: {
            type: 'campaign_name',
          },
        },
      }),
    );
  }

  submitForm() {
    if (!this.formGroup.valid) {
      return false;
    }
    const campaignType = this.formGroup.get('campaign_type').value;
    const recruitEnabled = this.formGroup.get('recruit').value;
    if (this.state === ECampaignCreateState.EDIT && recruitEnabled && this.campaignId) {
      this.ensureRecruitLinkBeforeSubmit(campaignType);
      return;
    }

    this.dispatchCampaignSave(campaignType);
  }

  private ensureRecruitLinkBeforeSubmit(campaignType: ECampaignType) {
    this.campaignService.getCampaignContents(this.campaignId).pipe(take(1)).subscribe(
      (contents: IContent[]) => {
        if (!this.hasRecruitLink(contents)) {
          this.store.dispatch(openSnackbar({
            snackbarData: {
              data: {
                message: 'At least one content should contain Recruit link',
                type: 'warning',
              },
            },
          }));
          return;
        }
        this.dispatchCampaignSave(campaignType);
      },
      () => this.dispatchCampaignSave(campaignType),
    );
  }

  private dispatchCampaignSave(campaignType: ECampaignType) {
    if (this.state === ECampaignCreateState.EDIT) {
      this.store.dispatch(
        updateCampaign({
          campaign: {
            ...this.campaignInfo,
            campaign_name: this.formGroup.get('campaign_name').value,
            campaign_type: campaignType,
            campaign_frequency: campaignType === ECampaignType.STANDARD ? null : this.formGroup.get('campaign_frequency').value,
            is_campaign_recruit: this.formGroup.get('recruit').value,
          },
        }),
      );
    } else {
      this.store.dispatch(
        createCampaign({
          campaign_name: this.formGroup.get('campaign_name').value,
          campaign_type: campaignType,
          campaign_frequency: campaignType === ECampaignType.STANDARD ? null : this.formGroup.get('campaign_frequency').value,
          is_campaign_recruit: this.formGroup.get('recruit').value,
        }),
      );
    }
  }

  private hasRecruitLink(contents: IContent[]): boolean {
    const placeholder = '{recruit_link}';
    return contents.some(content => {
      const values = [
        content.content_attention,
        content.first_benefit,
        content.second_benefit,
        content.third_benefit,
        content.content_details,
      ];
      return values.some(value => (value || '').toString().includes(placeholder));
    });
  }

  private resolveRecruitFlag(): boolean {
    const candidates: Array<boolean | string | number | null | undefined> = [
      this.campaignInfo?.campaign_recruit,
      this.campaignInfo?.is_campaign_recruit,
      this.campaignInfo?.recruit,
    ];

    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null) {
        continue;
      }

      if (typeof candidate === 'string' || typeof candidate === 'number') {
        const normalized = candidate.toString().toLowerCase().trim();
        return ['true', '1', 'yes', 'y', 't'].includes(normalized);
      }

      return !!candidate;
    }

    if (this.state === ECampaignCreateState.CREATE) {
      return !!this.sessionData?.c2recruit;
    }

    return false;
  }

  getCreateButtonText() {
    return this.state === ECampaignCreateState.CREATE
      ? 'Create Campaign'
      : 'Update Campaign';
  }
}
